import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { axisProps, CURSOR, gridProps, Legend, makeTooltip, type ChartDatum, type SeriesDef } from './parts';
import { compact } from '../../lib/format';

interface Props {
  data: readonly ChartDatum[];
  series: SeriesDef[];
  categoryKey: string;
  /** Horizontal reads better for long category names. */
  layout?: 'vertical' | 'horizontal';
  stacked?: boolean;
  /**
   * Set when a stack mixes positive and negative values. Recharts otherwise
   * emits rects with negative height, which SVG will not draw — the sign
   * offset splits the stack around a zero baseline instead.
   */
  diverging?: boolean;
  height?: number;
  valueFormat?: (n: number) => string;
  /** Formats the category tick — dates need it, plain labels do not. */
  categoryFormat?: (v: string) => string;
  categoryWidth?: number;
  /** False for counts, so a 0–2 axis stops repeating "1, 1, 2, 2". */
  allowDecimals?: boolean;
}

export function BarSeries({
  data,
  series,
  categoryKey,
  layout = 'vertical',
  stacked = false,
  diverging = false,
  height = 260,
  valueFormat = compact,
  categoryFormat,
  categoryWidth = 120,
  allowDecimals = true,
}: Props) {
  const Tip = makeTooltip((l) => (categoryFormat ? categoryFormat(l) : l), (v) => valueFormat(v));
  const horizontal = layout === 'horizontal';
  const last = series.length - 1;

  /** True when every value for this series is ≤ 0, i.e. it hangs below zero. */
  const isNegativeSeries = (key: string) =>
    diverging &&
    data.every((d) => {
      const v = (d as Record<string, unknown>)[key];
      return typeof v !== 'number' || v <= 0;
    });

  return (
    <div className="chart-wrap">
      <Legend series={series} />
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data as ChartDatum[]}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 4, right: 12, bottom: 0, left: 0 }}
          barCategoryGap={horizontal ? '22%' : '28%'}
          // 2px of surface between adjacent bars in a group.
          barGap={2}
          stackOffset={diverging ? 'sign' : 'none'}
        >
          <CartesianGrid {...gridProps} vertical={horizontal} horizontal={!horizontal} />
          {horizontal ? (
            <>
              <XAxis type="number" {...axisProps} tickFormatter={valueFormat} tickMargin={8} allowDecimals={allowDecimals} />
              <YAxis
                type="category"
                dataKey={categoryKey}
                {...axisProps}
                width={categoryWidth}
                tickMargin={8}
                tickFormatter={categoryFormat}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey={categoryKey}
                {...axisProps}
                tickMargin={8}
                tickFormatter={categoryFormat}
              />
              <YAxis {...axisProps} tickFormatter={valueFormat} width={46} tickMargin={6} allowDecimals={allowDecimals} />
            </>
          )}
          {diverging ? <ReferenceLine y={0} stroke="var(--axis)" strokeWidth={1} /> : null}
          <Tooltip content={Tip} cursor={CURSOR} />
          {series.map((s, i) => {
            // Round only the data-end of the bar so it stays anchored to the
            // baseline; on a stack only the outermost segment gets the round.
            const isEnd = !stacked || i === last || diverging;
            const below = isNegativeSeries(s.key);
            const radius: [number, number, number, number] = horizontal
              ? isEnd
                ? below
                  ? [4, 0, 0, 4]
                  : [0, 4, 4, 0]
                : [0, 0, 0, 0]
              : isEnd
                ? below
                  ? [0, 0, 4, 4]
                  : [4, 4, 0, 0]
                : [0, 0, 0, 0];
            return (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                fill={s.color}
                stackId={stacked ? 'a' : undefined}
                radius={radius}
                // 2px surface gap between stacked segments.
                stroke={stacked ? 'var(--surface-1)' : undefined}
                strokeWidth={stacked ? 2 : 0}
                maxBarSize={horizontal ? 22 : 46}
                isAnimationActive={false}
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
